/**
 * Ngữ cảnh đăng nhập + phân quyền.
 *
 * Thứ bậc quyền giống hệt server (server/src/api/auth.ts):
 *   VIEWER < OPERATOR < MANAGER < ADMIN < SUPER_ADMIN
 *
 * Lưu ý quan trọng: ẩn nút chỉ là phép lịch sự với người dùng, KHÔNG phải bảo mật.
 * Server vẫn kiểm tra quyền ở mọi route — phần này chỉ để người dùng khỏi bấm vào
 * thứ chắc chắn sẽ bị từ chối.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { EVENT_BI_CHAN, EVENT_ROI_HANG, EVENT_SESSION_LOST, api, login as apiLogin, logout as apiLogout, tokenStore } from './api';
import { realtime } from './ws';
import { ROLE_RANK, type AuthUser, type Role } from './types';
import { coQuyen, quyetDinhCoTuyen, NHAN_KHOA, type CoTuyen, type QuyetDinhCoTuyen } from './quyen';
import { BocDangDo, CAU_DANG_KIEM_TICK, Khoa } from '../components/Khoa';

interface AuthContextValue {
  user: AuthUser | null;
  /** Đang kiểm tra token lưu sẵn lúc mở trang */
  loading: boolean;
  signIn: (username: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  /**
   * ⛔ `hasRole` CÒN LẠI ĐÚNG MỘT VIỆC: nhận ra `SUPER_ADMIN` (khu vực quản trị hệ thống).
   *   Mọi nút HÀNH ĐỘNG nay hỏi `coQuyen` / `coCoTuyen` — `B4` mục 3. Không xoá hẳn vì
   *   `RequireQuyen` cho nhóm `SUPER_ADMIN` vẫn cần một câu hỏi về hạng, và vì hạng vẫn là
   *   thứ máy chủ dùng cho ba tuyến ấy.
   */
  hasRole: (min: Role) => boolean;
  /**
   * 🔴 CỬA LỊCH SỰ, KHÔNG PHẢI CỬA QUYỀN — cửa thật ở máy chủ (`requireQuyen`). Hai bên đọc
   *   CÙNG một nguồn (`user.quyen` từ `/api/auth/me`) và CÙNG một luật (`lib/quyen.ts`, có
   *   phép canh buộc khớp với `services/quyen.ts`).
   */
  coQuyen: (khoa: string) => boolean;
  /**
   * Cờ theo TUYẾN. `routeId` rỗng/`null` (tủ chưa gán tuyến) ⇒ `'khong-co-tuyen'`.
   * ➕ `GD-09` — thêm ca `'chua-biet'`: xem `daBietQuyen` ngay dưới.
   */
  coCoTuyen: (co: CoTuyen, routeId: string | null | undefined)
    => QuyetDinhCoTuyen | 'khong-co-tuyen' | 'chua-biet';
  /**
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-09` mục 3 + 4 — **ĐÃ HỎI XONG MÁY CHỦ VỀ QUYỀN CHƯA**. Luật số 0, lần này ở tầng
   * quyền — và đây là gốc CHUNG của hai lỗi lee chụp 14/09, tưởng là hai:
   *
   *   · thẻ tuyến HHG ghi "🔒 Chỉ xem", nhưng vào tủ thì công tắc MỞ;
   *   · `khach_hang_11` ở Tổng quan không thấy "Điều khiển theo tuyến", sang trang tủ thì thấy.
   *
   * 🔴 CẢ HAI LÀ MỘT: lần vẽ ĐẦU TIÊN, `user` lấy từ `localStorage` — một bản chụp cũ có thể
   *   **thiếu hẳn `routeAccess`** (bản ấy lưu từ một lượt đăng nhập trước khi trường này có).
   *   `(user?.routeAccess ?? [])` biến "máy chủ chưa nói" thành "không có tuyến nào", và
   *   `quyetDinhCoTuyen` trả `'chua-gan-tuyen'` — một câu KHẲNG ĐỊNH. Vài trăm mili-giây sau
   *   `/api/auth/me` trả lời, `routeAccess` về, và mọi thứ đổi. Người dùng thấy hai màn hình
   *   nói hai điều, và không màn hình nào tự nhận là đang đoán.
   *
   * ⛔ `?? []` LÀ CHỖ HỎNG, và nó là một dòng tôi tự viết ra để "cho an toàn". Nó an toàn theo
   *   nghĩa không làm vỡ mã, và KHÔNG an toàn theo nghĩa nó **dựng một câu trả lời cho một câu
   *   hỏi chưa đo được** — đúng Luật số 0, ở đúng chỗ đắt nhất: cái quyết định người dùng được
   *   làm gì.
   * ⇒ `daBietQuyen` là vế THỨ BA. Chưa biết thì nói chưa biết; màn hình vẽ một trạng thái
   *   TRUNG TÍNH (xám "đang kiểm quyền"), **không** vẽ hổ phách "bạn không có quyền" — vì hổ
   *   phách là một lời khai, và ta chưa có gì để khai.
   *
   * ⚠ `loading` một mình KHÔNG đủ: nó chỉ nói "đang gọi `/me`". Một `user` từ `localStorage`
   *   thiếu `routeAccess` thì kể cả khi `loading` đã tắt, ta vẫn chưa biết gì về tuyến. Nên
   *   phép này hỏi CẢ HAI: đã gọi xong, VÀ máy chủ có thật sự nói ra hai mảng ấy.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  daBietQuyen: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => tokenStore.user);
  const [loading, setLoading] = useState(true);
  /** Phiên vừa bị hạ quyền — hiện một dải cho tới khi người dùng đóng nó. */
  const [roiHang, setRoiHang] = useState<{ vaiCu: string; vaiMoi: string; loi: string } | null>(null);
  /** Tài khoản bị chặn theo HẠN — phiên chết, và đăng nhập lại không cứu được. */
  const [biChan, setBiChan] = useState<{ ma: string; cau: string; ten: string | null } | null>(null);

  // Mở trang: nếu còn token thì hỏi lại server xem còn hiệu lực không.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!tokenStore.access) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.get<AuthUser>('/api/auth/me');
        if (!alive) return;
        if (me) {
          tokenStore.saveUser(me);
          setUser(me);
          realtime.start();
        }
      } catch {
        if (!alive) return;
        tokenStore.clear();
        setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Token hỏng / refresh thất bại ở bất kỳ đâu → về màn đăng nhập.
  useEffect(() => {
    const onLost = () => {
      setUser(null);
      realtime.stop();
    };
    window.addEventListener(EVENT_SESSION_LOST, onLost);
    return () => window.removeEventListener(EVENT_SESSION_LOST, onLost);
  }, []);

  /*
   * 🔴 RƠI HẠNG — khác hẳn MẤT PHIÊN, nên là một sự kiện khác và một cách xử lý khác:
   *   mất phiên  ⇒ đá về màn đăng nhập
   *   rơi hạng   ⇒ phiên VẪN SỐNG, chỉ là quyền thấp đi. Đá ra là sai; im lặng cũng sai.
   *
   * ⚠ Phải cập nhật `user.role` ngay: nếu không, giao diện vẫn vẽ nút của ADMIN trong khi
   *   máy chủ đã trả 403 cho từng cú bấm — đúng cái cảnh "nút không ăn" mà dev_device nêu.
   */
  useEffect(() => {
    const onRoiHang = (e: Event) => {
      const d = (e as CustomEvent).detail as { vaiCu: string; vaiMoi: string; loi: string };
      setUser((u) => (u ? { ...u, role: d.vaiMoi as Role } : u));
      setRoiHang(d);
    };
    window.addEventListener(EVENT_ROI_HANG, onRoiHang);
    return () => window.removeEventListener(EVENT_ROI_HANG, onRoiHang);
  }, []);

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * 🔴 BỊ CHẶN THEO HẠN — `B4` mục 4. Ba việc, đúng thứ tự ấy:
   *   ① GIỮ LẠI CÂU CỦA MÁY CHỦ rồi mới ② dọn phiên ③ đưa về đăng nhập.
   *
   * ⛔ Đảo thứ tự là mất câu: dọn phiên trước thì `RequireAuth` chuyển trang ngay, và dải chữ
   *   chưa kịp vẽ. Người dùng thấy mình bị đá ra không lý do — đúng cái vòng lặp "gõ lại mật
   *   khẩu, lại bị đá ra".
   * ⚠ Dải này KHÔNG tự tắt và KHÔNG có nút đóng: nó là câu duy nhất giải thích vì sao màn hình
   *   đăng nhập đang từ chối họ. Nó tắt khi người dùng đăng nhập lại được — tức là khi có
   *   người đã gia hạn cho họ.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  useEffect(() => {
    const onChan = (e: Event) => {
      const d = (e as CustomEvent).detail as { ma: string; cau: string; ten: string | null };
      setBiChan(d);
      tokenStore.clear();
      setUser(null);
      realtime.stop();
    };
    window.addEventListener(EVENT_BI_CHAN, onChan);
    return () => window.removeEventListener(EVENT_BI_CHAN, onChan);
  }, []);

  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-09b` 14/09/2026 — ĐĂNG NHẬP XONG THÌ **HỎI `/me`**, y như `changePassword` vẫn làm.
   *
   * 🔴 LỖI ĐƯỢC SỬA: bản cũ đặt thẳng `res.user` — thứ `/api/auth/login` trả về — làm `user`.
   *   Phản hồi ấy có SÁU trường, không có `quyen`, không có `routeAccess`. `daBietQuyen` hỏi
   *   `Array.isArray(user.routeAccess)`, nên sau đăng nhập mà **chưa F5** nó không bao giờ
   *   true: mọi thẻ tuyến treo ở "đang kiểm quyền", mọi mục menu theo cờ treo ở `'chua-biet'`.
   *   Chỉ khi người dùng tải lại trang, `useEffect` mở trang mới gọi `/me` và mọi thứ đúng.
   *
   * ⛔ HAI VẾ, VÀ CẢ HAI ĐỀU CẦN — không vế nào thừa:
   *     · máy chủ (`auth.ts`, `CHON_TOI`) trả **đúng hình** ở cửa đăng nhập  → hết lỗi ở gốc;
   *     · chỗ này vẫn hỏi `/me`                                             → đúng **một**
   *       nguồn cho `user` trên cả ba đường vào (mở trang · đăng nhập · đổi mật khẩu).
   *   Bỏ vế hai thì hình dạng của `user` lại phụ thuộc **đường nào đưa nó vào** — chính cái
   *   máy đẻ ra lỗi này. Bỏ vế một thì mỗi lượt đăng nhập tốn thêm một vòng mạng để sửa một
   *   phản hồi vừa mới nhận, và một máy chủ cũ vẫn lọt.
   *
   * ⚠ `/me` HỎNG thì lùi về `res.user` — KHÔNG ném lỗi. Người dùng đã đăng nhập được thật; đá
   *   họ ra vì một vòng mạng phụ là sai. Với máy chủ đã sửa thì `res.user` đủ hình nên không
   *   mất gì; với máy chủ cũ thì `daBietQuyen` ở nguyên `false` và màn hình nói "đang kiểm
   *   quyền" — chưa biết thì nói chưa biết, KHÔNG khai bừa (Luật số 0).
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  const signIn = useCallback(async (username: string, password: string) => {
    const res = await apiLogin(username, password);
    let toi = res.user;
    try {
      const me = await api.get<AuthUser>('/api/auth/me');
      if (me) {
        tokenStore.saveUser(me);
        toi = me;
      }
    } catch { /* xem khối chú thích ngay trên: lùi về `res.user`, không đá người dùng ra */ }
    setUser(toi);
    realtime.start();
    return toi;
  }, []);

  const signOut = useCallback(async () => {
    realtime.stop();
    await apiLogout();
    setUser(null);
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    await api.post('/api/auth/change-password', { oldPassword, newPassword });
    const me = await api.get<AuthUser>('/api/auth/me');
    if (me) {
      tokenStore.saveUser(me);
      setUser(me);
    }
  }, []);

  const hasRole = useCallback(
    (min: Role) => (user ? ROLE_RANK[user.role] >= ROLE_RANK[min] : false),
    [user]
  );

  /*
   * ⚠ `user.quyen === undefined` = **MÁY CHỦ KHÔNG NÓI** (bản cũ), không phải "không có quyền
   *   nào". Xử lý đúng MỘT chỗ, ở đây: coi như mảng rỗng để không vẽ nút bừa — nhưng vẫn cho
   *   `xem_chan_doan` đi qua (nó luôn có), và `SUPER_ADMIN` vẫn qua bằng vế bỏ qua trong
   *   `coQuyen()`. Rải `?? []` khắp nơi là rải cả quyết định ấy khắp nơi.
   */
  const coQuyenCb = useCallback(
    (khoa: string) => coQuyen(user?.role ?? null, khoa, user?.quyen ?? []),
    [user],
  );

  /*
   * ⛔ `Array.isArray` chứ không `!= null`: một `routeAccess` là `null` hay là một thứ khác
   *   mảng đều có nghĩa "máy chủ không nói ra được", và cả hai đều KHÔNG được đọc thành rỗng.
   * ⚠ `SUPER_ADMIN` KHÔNG cần `routeAccess` — nó qua mọi cổng bằng vế bỏ qua, nên với nó thì
   *   "đã biết" chỉ cần `/me` đã trả lời.
   */
  const daBietQuyen = !loading && !!user
    && (user.role === 'SUPER_ADMIN' || (Array.isArray(user.quyen) && Array.isArray(user.routeAccess)));

  const coCoTuyenCb = useCallback(
    (co: CoTuyen, routeId: string | null | undefined) => {
      /*
       * 🔴 THỨ TỰ NÀY LÀ MỘT QUYẾT ĐỊNH: hỏi "đã biết chưa" TRƯỚC "có tuyến không".
       *   Đảo lại thì một tủ chưa gán tuyến sẽ trả `'khong-co-tuyen'` ngay cả lúc ta còn chưa
       *   đọc nổi cái tủ ấy — lại là một câu khẳng định dựng từ chỗ chưa đo được.
       */
      if (!daBietQuyen) return 'chua-biet' as const;
      if (!routeId) return 'khong-co-tuyen' as const;
      if (user?.role === 'SUPER_ADMIN') return 'cho-qua' as const;
      const hang = (user?.routeAccess ?? []).find((r) => r.routeId === routeId);
      return quyetDinhCoTuyen(user?.role ?? null, co, hang ?? null);
    },
    [user, daBietQuyen],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, signIn, signOut, changePassword, hasRole, daBietQuyen,
             coQuyen: coQuyenCb, coCoTuyen: coCoTuyenCb }),
    [user, loading, signIn, signOut, changePassword, hasRole, daBietQuyen, coQuyenCb, coCoTuyenCb]
  );

  return (
    <AuthContext.Provider value={value}>
      {/*
        * ⚠ Dải này ĐÈ LÊN TẤT CẢ và KHÔNG tự tắt: quyền vừa đổi dưới chân người đang trực
        *   ca. Nó không phải một `toast` trôi qua trong 3 giây — người ta đang bấm nút, và
        *   câu họ cần đọc là "nút không ăn KHÔNG phải app hỏng".
        */}
      {biChan && (
        <div className="fixed inset-x-0 top-0 z-[70] flex items-start gap-3 border-b border-nguy
                        bg-nguy-nen px-4 py-2 text-sm text-nguy">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-nguy" />
          <span className="flex-1">
            <b className="text-nguy">
              {biChan.ma === 'TAI_KHOAN_BI_KHOA' ? 'Tài khoản đã bị khoá'
                : biChan.ma === 'TAI_KHOAN_HET_HAN' ? 'Tài khoản đã hết hạn'
                : biChan.ma === 'KHACH_HANG_HET_HAN' ? 'Tài khoản cấp trên đã hết hạn hoặc bị khoá'
                : 'Tài khoản không dùng được'}
            </b>
            {' — '}{biChan.cau}
            {/* ⚠ Nói rõ đăng nhập lại KHÔNG cứu được: không có câu này thì người ta sẽ thử. */}
            {' '}Đăng nhập lại sẽ không mở được — cần có người gia hạn hoặc mở khoá tài khoản.
          </span>
        </div>
      )}
      {roiHang && (
        <div className="fixed inset-x-0 top-0 z-[60] flex items-start gap-3 border-b border-canh
                        bg-canh-nen px-4 py-2 text-sm text-canh">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-canh" />
          <span className="flex-1">
            <b className="text-chu-1">Phiên của bạn vừa bị hạ quyền</b>
            {' — '}{roiHang.vaiCu ? `"${roiHang.vaiCu}"` : 'vai trò'} không đọc được,
            {' '}phiên đang chạy với quyền <b className="text-chu-1">{roiHang.vaiMoi}</b>.
            {' '}{roiHang.loi}
          </span>
          <button
            type="button" className="shrink-0 underline hover:text-chu-1"
            onClick={() => setRoiHang(null)}
          >Đóng</button>
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth phải nằm trong <AuthProvider>');
  return ctx;
}

/** Tiện dụng khi chỉ cần biết "quyền của tôi có đủ không" — CHỈ còn dùng cho nhóm `SUPER_ADMIN`. */
export function useCan(min: Role): boolean {
  return useAuth().hasRole(min);
}

/**
 * `lượt 58` — TÊN NGƯỜI CẤP QUYỀN cho tài khoản đang đăng nhập, hoặc `null`.
 *
 * 🔴 Ba giá trị, ba nghĩa — và đây là chỗ DUY NHẤT ép chúng về hai (`string | null`), vì phía
 *   sau nó chỉ còn một câu hỏi: *"có tên để in không"*.
 *     `me` chưa nạp        → `null`  (băng lùi về câu chung, không chớp một cái tên rồi mất)
 *     `nguoiTao === undefined` → `null`  (máy chủ bản cũ KHÔNG NÓI — không được bịa)
 *     `nguoiTao === null`  → `null`  (tài khoản GỐC: không có ai để hỏi)
 *
 * ⚠ Ba ca gộp được Ở ĐÂY vì cùng dẫn tới một câu chữ. Chúng KHÔNG gộp được ở việc 2d (cây tài
 *   khoản), nơi `null` (gốc thật) phải khác `null`-mà-không-phải-SUPER_ADMIN (ca `@jlee`) —
 *   nên chỗ ấy đọc thẳng `createdById`, không đi qua hàm này.
 */
export function useNguoiCap(): string | null {
  return useAuth().user?.nguoiTao?.username ?? null;
}

/** `B4` mục 3 — hỏi TICK. Đây là cửa mặc định cho mọi nút hành động. */
export function useQuyen(khoa: string): boolean {
  return useAuth().coQuyen(khoa);
}

/** `B4` mục 3 — hỏi CỜ THEO TUYẾN. Trả về QUYẾT ĐỊNH, không phải `boolean`: bốn ca, bốn câu. */
export function useCoTuyen(co: CoTuyen, routeId: string | null | undefined) {
  return useAuth().coCoTuyen(co, routeId);
}

/**
 * Ẩn nội dung với vai trò thấp hơn `role`.
 *
 *   <Can role="MANAGER"><button>Đẩy lịch</button></Can>
 *
 * Muốn hiện nút mờ kèm lời giải thích thay vì ẩn hẳn thì truyền `fallback`.
 */
export function Can({
  role, children, fallback = null,
}: {
  role: Role;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const ok = useCan(role);
  return <>{ok ? children : fallback}</>;
}

/**
 * Ẩn nội dung khi thiếu TICK.
 *
 *   <CoQuyen khoa="chien_luoc"><button>Áp chiến lược</button></CoQuyen>
 *
 * ⚠ Thay `<Can role="…">` ở mọi nút HÀNH ĐỘNG (`B4` mục 3). `<Can>` chỉ còn cho khu vực của
 *   `SUPER_ADMIN` — nơi máy chủ cũng còn dùng hạng.
 */
export function CoQuyen({
  khoa, children, fallback, an = false, hienHuyHieu = true,
}: {
  khoa: string;
  children: ReactNode;
  /** Vẽ thay thế do chỗ gọi tự chọn. Bỏ trống ⇒ **KHOÁ** (mờ + ổ khoá), KHÔNG ẩn. */
  fallback?: ReactNode;
  /** `true` ⇒ ẩn hẳn. Chỉ dùng khi cái nút ấy vô nghĩa với người không có quyền. */
  an?: boolean;
  hienHuyHieu?: boolean;
}) {
  const ok = useQuyen(khoa);
  const { daBietQuyen } = useAuth();
  if (ok) return <>{children}</>;
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ➕ `GD-09b` — CHƯA HỎI XONG THÌ CHƯA ĐƯỢC KHAI, **cả ở tầng TICK**.
   *
   * 🔴 `coQuyen()` đọc `user?.quyen ?? []`, nên lúc `/me` chưa trả lời nó nói "không có tick
   *   nào" — và cái `false` ấy rơi thẳng xuống dòng `<Khoa>` bên dưới, vẽ ra hổ phách *"Không
   *   có quyền: …"*. Đúng cái `GD-09` đã cắt ở tầng CỜ TUYẾN mà bỏ sót ở tầng này.
   * ⛔ ĐẶT SAU `if (ok)`, KHÔNG ĐẶT TRƯỚC: `SUPER_ADMIN` và khoá `xem_chan_doan` qua được
   *   `coQuyen()` ngay cả khi `quyen` chưa về; chặn chúng lại một nhịp là bắt một người CÓ
   *   quyền phải chờ một câu trả lời họ không cần.
   * ⚠ `an` và `fallback` KHÔNG đổi: chúng là lựa chọn của chỗ gọi cho ca "đã đo, không có
   *   quyền"; ở đây ta còn chưa đo, nên câu đúng là câu xám, không phải câu của họ.
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  if (!daBietQuyen && !an) {
    return <BocDangDo hienHuyHieu={hienHuyHieu} cau={CAU_DANG_KIEM_TICK}>{children}</BocDangDo>;
  }
  /*
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * 🔴 KHOÁ, KHÔNG ẨN — lee chốt 10/09. Và đây là chỗ ĐỔI HÀNH VI MẶC ĐỊNH, nên nói rõ vì sao:
   *   ẩn hẳn thì người dùng không biết **chức năng ấy có tồn tại**, nên cũng không biết mình
   *   thiếu gì để đi xin. Một cái nút khoá là một câu; một khoảng trống thì không nói gì.
   * ⛔ Và vẽ nó ở ĐÂY, qua `<Khoa>` — không để mỗi trang tự bôi xám. `[95]` canh điều đó.
   * ⚠ `an` vẫn còn cho ca hiếm: nút mà người không có quyền **không cần biết là có** (vd một
   *   thao tác chỉ dành cho quản trị hệ thống, hiện ra chỉ gây tò mò vô ích).
   * ═══════════════════════════════════════════════════════════════════════════════════════
   */
  if (an) return <>{fallback ?? null}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return <Khoa ten={NHAN_KHOA[khoa] ?? khoa} hienHuyHieu={hienHuyHieu}>{children}</Khoa>;
}
